import { Observable } from "rxjs";
import { BaseHttpService } from "./base.http.service";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
    CreateApplicationPayload,
    CreateRedemptionOrderPayload,
    InvestmentPlan, EquityOrderPayload,
    TermDepositOrderPayload, OrderAction, ProductFee
} from "src/app/models/orders";
import { AppConfig } from 'src/app/app.config';
import { ClientEntity } from "src/app/models/clients";
import { IEnvironmentConfig } from "src/app/models/environmentConfig";
import {AmountViewModel} from "../../models/amount";
import {SecurityQuote} from "../../models/securityQuote";
import * as moment from "moment/moment";
import {CalculateUnitsResponse} from "../../models/calculateUnits";

@Injectable()
export class TradingService extends BaseHttpService {
    constructor(private http: HttpClient, private appConfig: AppConfig) {
        super();
    }

    get envSettings(): IEnvironmentConfig {
        return this.appConfig.getEnvironmentConfig();
    }

  public getProducts(portfolioId: number): Observable<any> {
      const url = `${this.envSettings.webApiUrl}products?portfolioId=${portfolioId}&active=true&excludeNonTradeableSecurities=true`;
      return this.http.get(url, {
          observe: "body",
          responseType: "json",
          headers: this.getHttpHeaders()
      });
    }

    public getPortfoliosOrders(selectedEntities: ClientEntity[]): Observable<any> {
        let entityQueryString = "entityIds=" + selectedEntities.map(x => x.EntityId).join("&entityIds=");
        const url = `${this.envSettings.webApiUrl}portfolios/orders?${entityQueryString}`;
        return this.http.get(url, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });
    }

    public getPdsLink(productLinkToken: string): string {
        return `${this.envSettings.webApiUrl}documents?token=${encodeURIComponent(productLinkToken)}`;
    }

    public createApplication(
        portfolioId: number,
        body: CreateApplicationPayload
    ): Observable<any> {
        const jsonBody = JSON.stringify(body);
        const url = `${this.envSettings.webApiUrl}portfolio/${portfolioId}/applications`;
        return this.http.post(url, jsonBody, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });
    }

    public createRedemption(
        portfolioId: number,
        body: CreateRedemptionOrderPayload
    ): Observable<any> {
        const jsonBody = JSON.stringify(body);
        const url = `${this.envSettings.webApiUrl}portfolio/${portfolioId}/redemptions`;
        return this.http.post(url, jsonBody, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });
    }

    public cancelPendingOrder(
        portfolioId: number,
        instructionId: number
    ): Observable<any> {
        const url = `${this.envSettings.webApiUrl}portfolio/${portfolioId}/instructions/${instructionId}/cancel`;
        return this.http.post(url, {}, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });
    }

    public approveOrder(
        portfolioId: number,
        instructionId: number
    ): Observable<any> {
        const url = `${this.envSettings.webApiUrl}portfolio/${portfolioId}/instructions/${instructionId}/approve`;
        return this.http.post(url, {}, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });
    }

    public getRegularInvestmentPlan(portfolioId: number): Observable<any> {
        const url = `${this.envSettings.webApiUrl}portfolio/${portfolioId}/investmentPlan`;
        return this.http.get(url, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });
    }

    public saveRegularInvestmentPlan(investmentPlan: InvestmentPlan): Observable<any> {
        const jsonBody = JSON.stringify(investmentPlan);
        const url = `${this.envSettings.webApiUrl}portfolio/${investmentPlan.portfolioId}/investmentPlan`;
        return this.http.post(url, jsonBody, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });
    }

    public createEquityOrder(portfolioId: number, sellOrder: EquityOrderPayload) {

        const jsonBody = JSON.stringify(sellOrder);
        const url = `${this.envSettings.webApiUrl}portfolio/${portfolioId}/equityOrder`;
        return this.http.post(url, jsonBody, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });
    }

    public createTermDepositOrder(portfolioId: number, termDepositOrderPayload: TermDepositOrderPayload){
        const url = `${this.envSettings.webApiUrl}portfolio/${portfolioId}/termdeposit`;
        return this.http.post(url, termDepositOrderPayload, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });
    }

    public getFeesForProduct(portfolioId: number, productId: number, orderAction: OrderAction = null): Observable<ProductFee[]>{
        const url = `${this.envSettings.webApiUrl}products/${productId}/fees?portfolioId=${portfolioId}${orderAction ? `&action=${orderAction}` : ``}`;
        return this.http.get<ProductFee[]>(url, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });
    }

    public getLatestPrice(securityId: number): Observable<SecurityQuote>{
        const url = `${this.envSettings.webApiUrl}securities/${securityId}/quotes/latest`;
        return this.http.get<SecurityQuote>(url, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });
    }

    public calculateFee(portfolioId: number, securityId: number, productId: number, amount: AmountViewModel, orderAction: OrderAction){


        let body = {
            portfolioId: portfolioId,
            securityId: securityId,
            orderAction: orderAction,
            amount: amount
        }

        const url = `${this.envSettings.webApiUrl}products/${productId}/calculateFees?portfolioId=${portfolioId}${orderAction ? `&orderAction=${orderAction}` : ``}`;
        return this.http.post<ProductFee[]>(url, body, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });

    }

    public calculateUnitsFromValue(securityId: number, asAt: string, amount: number, currency: string) {

        let body = {
            asAt: moment(asAt).format("YYYY-MM-DD"),
            amount: {
                value: amount,
                currency: currency,
                currencyCode: currency
            } as AmountViewModel
        }

        const url = `${this.envSettings.webApiUrl}securities/${securityId}/calculateUnits`;
        return this.http.post<CalculateUnitsResponse>(url, body, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });
    }


    public calculateValueOfUnits(securityId: number, asAt: string, units: number, currency: string): Observable<AmountViewModel> {

        let body = {
            asAt: moment(asAt).format("YYYY-MM-DD"),
            units: units,
            currency: currency
        }

        const url = `${this.envSettings.webApiUrl}securities/${securityId}/calculateValue`;
        return this.http.post<AmountViewModel>(url, body, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });
    }
}
